import {
  ErrorBoundary as SentryErrorBoundary,
  type FallbackRender,
} from '@sentry/react';
import { cn } from 'cn';
import { type PropsWithChildren, type ReactElement } from 'react';

import { CrosshatchFill } from '@vestigas/ui';

type P = PropsWithChildren & {
  readonly className?: string;
  readonly fallback?: ReactElement | FallbackRender | undefined;
  /**
   * Convenience prop for the standard crosshatch error UI.
   * Ignored when `fallback` is provided.
   */
  readonly fallbackMessage?: string;
  readonly limitHeight?: boolean;
};

/**
 * ErrorBoundary component that wraps its children with a Sentry.ErrorBoundary.
 *
 * Accepts either a custom `fallback` element/render-prop, or a plain
 * `fallbackMessage` string which renders the shared crosshatch error UI
 * (the same pattern used by `withErrorBoundary`).
 */
export const ErrorBoundary = ({
  children,
  className,
  fallback,
  fallbackMessage,
  limitHeight = false,
}: P) => {
  const resolvedFallback =
    fallback ??
    (fallbackMessage ? (
      <div
        className={cn(
          'relative flex h-full min-h-28 items-center justify-center rounded-md text-center',
          { 'max-h-28': limitHeight },
          className,
        )}
      >
        <CrosshatchFill className="stroke-tiga-semantic-error/40" />
        <span className="z-0 p-2 text-sm font-semibold text-tiga-semantic-error">
          {fallbackMessage}
        </span>
      </div>
    ) : undefined);

  return (
    <SentryErrorBoundary fallback={resolvedFallback} showDialog={false}>
      {children}
    </SentryErrorBoundary>
  );
};
